import { useContext, useState } from 'react'
import { Button, InlineAlert } from '../components/BaseComponents'
import { getApiErrorMessage, getCSRFToken } from '../lib/utils'
import usePublicApi from '../hooks/usePublicApi'
import { userContext } from '../context/UserContext'

type VerifyEmailRequiredProps = {
	email?: string
	fullName?: string
}

// Shown by UserContext instead of the app for a leftover session of an unverified account.
function VerifyEmailRequired({ email, fullName }: VerifyEmailRequiredProps) {
	const publicApi = usePublicApi()
	const { clearUserData, fetchUserData } = useContext(userContext)
	const [busy, setBusy] = useState(false)
	const [sent, setSent] = useState(false)
	const [error, setError] = useState('')

	const onResend = async () => {
		setBusy(true)
		setError('')
		setSent(false)
		try {
			await publicApi.post('/resendVerification', { email, _csrf: await getCSRFToken() })
			setSent(true)
		} catch (err) {
			setError(getApiErrorMessage(err, 'Could not send the verification email. Please try again later.'))
		} finally {
			setBusy(false)
		}
	}

	// For a link opened on another device: re-read the account state.
	const onRecheck = async () => {
		setBusy(true)
		setError('')
		try {
			await fetchUserData()
		} finally {
			setBusy(false)
		}
	}

	const onLogout = async () => {
		await publicApi.post('/user/logout', { _csrf: await getCSRFToken() })
		clearUserData()
		window.location.reload()
	}

	return (
		// Renders outside AppRouter's flex wrapper, so the flex container stops the inner mt-16 collapsing through #root.
		<div className='flex flex-col items-center min-h-dvh'>
			<div className='content w-[32rem] mt-16 text-center'>
				<div className='text-charcoal-100 text-2xl'>{fullName ? `Welcome to Neoni, ${fullName}` : 'Welcome to Neoni'}</div>
				<div className='mt-8 text-charcoal-100'>
					Verify your email address to continue. We sent a verification link{email && <> to <span>{email}</span></>}. Check your spam folder if you cannot find it.
				</div>
				{sent && (
					<InlineAlert type='success' className='mt-6'>If your address is not yet verified, a new verification email is on its way.</InlineAlert>
				)}
				{error && <InlineAlert type='error' className='mt-6'>{error}</InlineAlert>}
				<div className='mt-8 flex justify-center gap-4'>
					<Button type='Primary' text='Resend verification email' disabled={busy} onClick={onResend} />
					<Button type='Outlined' text='I have verified my email' disabled={busy} onClick={onRecheck} />
				</div>
				{email && (
					<div className='mt-8 text-charcoal-100'>
						Signed in as <span>{email}</span>.
					</div>
				)}
				<Button type='Outlined' text='Log out' additionalClass={email ? 'mt-6' : 'mt-8'} onClick={onLogout} />
			</div>
		</div>
	)
}

export default VerifyEmailRequired
